import React from 'react';
import { MapPin, Phone, Mail, ChevronRight } from 'lucide-react';
import { useContent } from '../context/ContentContext';

const Footer: React.FC = () => {
  const { content } = useContent();

  return (
    <footer id="contact" className="bg-slate-950 text-white pt-16 pb-8 border-t border-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12 mb-12">
          {/* Brand - Spans 4 columns on large screens */}
          <div className="col-span-1 md:col-span-2 lg:col-span-4">
            <div className="flex items-center gap-4 mb-6">
               {content.hero.logoUrl && (
                 <img 
                   src={content.hero.logoUrl} 
                   alt="Logo" 
                   className="h-10 sm:h-12 w-auto object-contain drop-shadow-sm flex-shrink-0" 
                 />
               )}
               <h3 className="text-lg sm:text-xl md:text-2xl font-bold whitespace-nowrap">{content.hero.companyNameCn}</h3>
            </div>
            
            <p className="text-slate-400 mb-6 max-w-sm leading-relaxed text-sm">
              {content.footer?.description || "我们致力于为客户提供专业、高效、合规的财税服务..."}
            </p>
          </div>

          {/* Links - Spans 3 columns on large screens */}
          <div className="lg:col-span-3">
            <h4 className="text-lg font-semibold mb-4 text-[#D4AF37]">快速导航</h4>
            <ul className="space-y-2">
              <li><a href="#home" className="text-slate-400 hover:text-[#D4AF37] flex items-center gap-1 transition-colors text-sm"><ChevronRight size={14}/> 首页</a></li>
              <li><a href="#about" className="text-slate-400 hover:text-[#D4AF37] flex items-center gap-1 transition-colors text-sm"><ChevronRight size={14}/> 公司简介</a></li>
              <li><a href="#services" className="text-slate-400 hover:text-[#D4AF37] flex items-center gap-1 transition-colors text-sm"><ChevronRight size={14}/> 业务范围</a></li>
              <li><a href="#experience" className="text-slate-400 hover:text-[#D4AF37] flex items-center gap-1 transition-colors text-sm"><ChevronRight size={14}/> 项目案例</a></li>
            </ul>
          </div>

          {/* Contact - Spans 5 columns on large screens */}
          <div className="lg:col-span-5">
            <h4 className="text-lg font-semibold mb-4 text-[#D4AF37]">联系我们</h4>
            <div className="flex flex-col sm:flex-row gap-8 items-start">
              <ul className="space-y-4 flex-1">
                <li className="flex items-start gap-3 group">
                  <MapPin className="text-[#D4AF37] mt-1 flex-shrink-0 group-hover:text-white transition-colors" size={18} />
                  <span className="text-slate-300 text-sm leading-relaxed group-hover:text-white transition-colors">
                    {content.contact?.address || "地址信息加载中..."}
                  </span>
                </li>
                <li className="flex items-center gap-3 group">
                  <Phone className="text-[#D4AF37] flex-shrink-0 group-hover:text-white transition-colors" size={18} />
                  <span className="text-slate-300 text-sm group-hover:text-white transition-colors">
                    {content.contact?.phone || "电话信息加载中..."}
                  </span>
                </li>
                <li className="flex items-center gap-3 group">
                  <Mail className="text-[#D4AF37] flex-shrink-0 group-hover:text-white transition-colors" size={18} />
                  <span className="text-slate-300 text-sm group-hover:text-white transition-colors">
                    {content.contact?.email || "邮箱信息加载中..."}
                  </span>
                </li>
              </ul>
              
              {/* QR Code Section */}
              {content.contact?.qrCodeUrl && (
                <div className="flex flex-col items-center sm:items-end flex-shrink-0 mt-4 sm:mt-0">
                  <div className="bg-white p-2 rounded-lg shadow-lg w-32 h-32 flex items-center justify-center">
                    <img 
                      src={content.contact.qrCodeUrl} 
                      alt={content.contact.qrCodeLabel || "QR Code"} 
                      className="w-full h-full object-contain"
                    />
                  </div>
                  {content.contact.qrCodeLabel && (
                    <span className="mt-2 text-xs text-slate-400 font-medium text-center">{content.contact.qrCodeLabel}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="border-t border-slate-900 pt-8 text-center text-slate-500 text-sm flex justify-center items-center flex-col md:flex-row gap-4">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <p>&copy; {new Date().getFullYear()} {content.hero.companyNameCn}. All rights reserved.</p>
            {content.footer?.icpText && (
              <a href={content.footer.icpLink} target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37] transition-colors">
                {content.footer.icpText}
              </a>
            )}
            {content.footer?.policeText && (
              <a href={content.footer.policeLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-[#D4AF37] transition-colors">
                <img src="https://beian.mps.gov.cn/img/logo01.dd7ff50e.png" alt="公安备案" className="w-4 h-4" />
                {content.footer.policeText}
              </a>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;